import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Loader2, UserPlus, ShieldCheck } from "lucide-react";

export default function UsersManager({ onUsersChanged }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const addUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", { name, email, password, role });
      toast.success("Member added");
      setName("");
      setEmail("");
      setPassword("");
      setRole("user");
      load();
      if (onUsersChanged) onUsersChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-slate-200" data-testid="manage-team-btn">
          <Users className="h-4 w-4 mr-1.5" /> Team
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Team members</DialogTitle>
        </DialogHeader>

        <div className="space-y-2" data-testid="team-list">
          {users.map((u) => (
            <div
              key={u.id}
              className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2"
              data-testid={`team-member-${u.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{u.name}</p>
                <p className="text-xs text-slate-500 truncate">{u.email}</p>
              </div>
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${
                  u.role === "admin"
                    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                    : "text-slate-600 bg-slate-50 border-slate-200"
                }`}
              >
                {u.role === "admin" && <ShieldCheck className="h-3 w-3" />}
                {u.role}
              </span>
            </div>
          ))}
        </div>

        <form onSubmit={addUser} className="space-y-4 pt-4 border-t border-slate-100" data-testid="add-user-form">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-slate-400">Add member</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="u-name">Name (optional)</Label>
              <Input id="u-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="user-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="u-role">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger id="u-role" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="u-email">Email (username)</Label>
            <Input
              id="u-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@example.com"
              required
              data-testid="user-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="u-password">Password</Label>
            <Input
              id="u-password"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Set an initial password (min 6 chars)"
              minLength={6}
              required
              data-testid="user-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-slate-900 hover:bg-slate-700 text-white"
            data-testid="add-user-submit-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><UserPlus className="h-4 w-4 mr-1.5" /> Add member</>)}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
